import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Divider,
  Popover,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterSelectInput from 'src/common/FilterSelectInput';
import DateFilterField from 'src/common/formFields/DateFilterField';

const TransactionFilters = ({
  anchorEl,
  onClose,
  onApply,
  onClear,
  isExternal,
  isIssued,
  siteOptions,
  tankOptions,
  externalVehicleOptions,
  draftVehicle,
  setDraftVehicle,
  draftStartDateTime,
  setDraftStartDateTime,
  draftEndDateTime,
  setDraftEndDateTime,
  draftSite,
  setDraftSite,
  draftTank,
  setDraftTank,
  filterError
}) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    PaperProps={{
      sx: {
        width: { xs: 'calc(100vw - 24px)', sm: 560 },
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: 'min(560px, calc(100vh - 96px))',
        overflow: 'auto',
        mt: 1,
        p: { xs: 2, sm: 2.5 },
        boxSizing: 'border-box',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 4
      }
    }}
  >
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <FilterAltOutlinedIcon color="primary" />
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" fontWeight={700}>Transaction filters</Typography>
        </Stack>
      </Stack>
      <Divider />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <FilterSelectInput
          label="Site"
          name="transaction_site_filter"
          value={draftSite}
          data={siteOptions}
          onInputChange={setDraftSite}
          sx={{ width: '100%', mr: 0 }}
        />
        {isIssued && (
          <FilterSelectInput
            label="Tank"
            name="transaction_tank_filter"
            value={draftTank}
            data={tankOptions}
            onInputChange={setDraftTank}
            sx={{ width: '100%', mr: 0 }}
          />
        )}
        <DateFilterField
          label={`Start Date Time${isExternal ? '' : ' *'}`}
          value={draftStartDateTime}
          onlyDate={false}
          onDateChange={(value) => setDraftStartDateTime(value)}
          onClearFilter={() => setDraftStartDateTime(null)}
          pickerProps={{
            maxDateTime: draftEndDateTime || undefined
          }}
          sx={{ width: '100%', minWidth: '100%' }}
        />
        <DateFilterField
          label={`End Date Time${isExternal ? '' : ' *'}`}
          value={draftEndDateTime}
          onlyDate={false}
          onDateChange={(value) => setDraftEndDateTime(value)}
          onClearFilter={() => setDraftEndDateTime(null)}
          pickerProps={{ minDateTime: draftStartDateTime || undefined }}
          sx={{ width: '100%', minWidth: '100%' }}
        />
        {isExternal && (
          <TextField
            size="small"
            label="Search Vehicle"
            value={draftVehicle}
            onChange={(event) => setDraftVehicle(event.target.value)}
            placeholder={externalVehicleOptions.length
              ? `e.g. ${externalVehicleOptions[0]}`
              : 'Enter vehicle number'}
            fullWidth
            sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}
          />
        )}
        {filterError && (
          <Typography variant="body2" color="error" sx={{ gridColumn: '1 / -1' }}>
            {filterError}
          </Typography>
        )}
      </Box>
      <Divider />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button variant="outlined" onClick={onClear}>Clear all</Button>
        <Button variant="contained" onClick={onApply}>Apply</Button>
      </Stack>
    </Stack>
  </Popover>
);

TransactionFilters.propTypes = {
  anchorEl: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  isExternal: PropTypes.bool.isRequired,
  isIssued: PropTypes.bool.isRequired,
  siteOptions: PropTypes.array.isRequired,
  tankOptions: PropTypes.array.isRequired,
  externalVehicleOptions: PropTypes.array.isRequired,
  draftVehicle: PropTypes.string.isRequired,
  setDraftVehicle: PropTypes.func.isRequired,
  draftStartDateTime: PropTypes.any,
  setDraftStartDateTime: PropTypes.func.isRequired,
  draftEndDateTime: PropTypes.any,
  setDraftEndDateTime: PropTypes.func.isRequired,
  draftSite: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setDraftSite: PropTypes.func.isRequired,
  draftTank: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setDraftTank: PropTypes.func.isRequired,
  filterError: PropTypes.string
};

TransactionFilters.defaultProps = {
  draftStartDateTime: null,
  draftEndDateTime: null,
  filterError: ''
};

export default TransactionFilters;
